import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { generatePaymentsUrl } from '../../helpers';
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { IProviderPayableLine, ProviderPayableLinePaymentStatusEnum } from 'features/salesOrder/types';
import { PaymentsModal } from './PaymentsModal';
import {
	PROVIDER_PAYABLE_LINE_STATUS_COLOR,
	PROVIDER_PAYABLE_LINE_STATUS_LABEL,
	PROVIDER_PAYABLE_LINE_TYPE_LABEL,
} from '../../../../../features/salesOrder/constants';
import { useProviderPayables } from '../../context/ProviderPayablesContext';

interface Props {
	saleOrderId: string;
}

export const ProviderPayablesTab = ({ saleOrderId }: Props): JSX.Element => {
	const { lines, loading, error, refresh } = useProviderPayables();
	const [selectedLine, setSelectedLine] = useState<IProviderPayableLine | null>(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [copiedLineId, setCopiedLineId] = useState<string | null>(null);

	const location = useLocation();

	const lineIdFromURL = useMemo(() => new URLSearchParams(location.search).get('lineId'), [location.search]);

	const handledAutoOpenLineIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (!lineIdFromURL) {
			handledAutoOpenLineIdRef.current = null;
			return;
		}

		if (lines.length === 0) return;
		if (handledAutoOpenLineIdRef.current === lineIdFromURL) return;

		const line = lines.find((l) => l.id === lineIdFromURL);
		if (!line) return;

		handledAutoOpenLineIdRef.current = lineIdFromURL;
		setSelectedLine(line);
		setModalOpen(true);
	}, [lineIdFromURL, lines]);

	const replaceUrlWithoutNavigation = (lineId?: string) => {
		const nextUrl = generatePaymentsUrl(saleOrderId, lineId);
		window.history.replaceState(window.history.state, '', nextUrl);
	};

	const handleCopyLineId = (event: React.MouseEvent, lineId: string) => {
		event.stopPropagation();
		navigator.clipboard.writeText(lineId);
		setCopiedLineId(lineId);
		setTimeout(() => {
			setCopiedLineId((current) => (current === lineId ? null : current));
		}, 2000);
	};

	const handleRowClick = (line: IProviderPayableLine) => {
		handledAutoOpenLineIdRef.current = line.id;
		setSelectedLine(line);
		setModalOpen(true);
		replaceUrlWithoutNavigation(line.id);
	};

	const handleModalClose = () => {
		setModalOpen(false);
		setSelectedLine(null);
		handledAutoOpenLineIdRef.current = null;
		replaceUrlWithoutNavigation();
	};

	const handlePaymentSaved = async () => {
		const currentSelectedLineId = selectedLine?.id;
		const refreshedLines = await refresh();

		if (!currentSelectedLineId) return;

		const updatedSelectedLine = refreshedLines.find((line) => line.id === currentSelectedLineId) ?? null;

		setSelectedLine(updatedSelectedLine);

		if (updatedSelectedLine) {
			handledAutoOpenLineIdRef.current = updatedSelectedLine.id;
		}
	};

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" py={6}>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" sx={{ m: 2 }}>
				{error}
			</Alert>
		);
	}

	const grouped = lines.reduce<Record<string, IProviderPayableLine[]>>((acc, line) => {
		const key = line.serviceId;
		if (!acc[key]) acc[key] = [];
		acc[key].push(line);
		return acc;
	}, {});

	const pendingCount = lines.filter((l) => l.paymentStatus === ProviderPayableLinePaymentStatusEnum.Pending).length;
	const partialCount = lines.filter((l) => l.paymentStatus === ProviderPayableLinePaymentStatusEnum.Partial).length;

	return (
		<Box sx={{ p: 1 }}>
			<Box display="flex" gap={2} mb={2} flexWrap="wrap">
				<Typography variant="subtitle1" fontWeight="bold" sx={{ mr: 1 }}>
					Pagos a Proveedores
				</Typography>
				{pendingCount > 0 && (
					<Chip label={`${pendingCount} pendiente${pendingCount > 1 ? 's' : ''}`} color="error" size="small" />
				)}
				{partialCount > 0 && (
					<Chip label={`${partialCount} parcial${partialCount > 1 ? 'es' : ''}`} color="warning" size="small" />
				)}
				{lines.length > 0 && pendingCount === 0 && partialCount === 0 && (
					<Chip label="Todo pagado" color="success" size="small" />
				)}
			</Box>

			{lines.length === 0 ? (
				<Box display="flex" flexDirection="column" alignItems="center" py={6} color="text.secondary">
					<MoneyOffIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
					<Typography variant="body2">No hay líneas pagables para esta orden.</Typography>
					<Typography variant="caption">Se generan automáticamente al crear servicios y tickets.</Typography>
				</Box>
			) : (
				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow sx={{ bgcolor: 'grey.50' }}>
								<TableCell padding="checkbox" />
								<TableCell>Tipo</TableCell>
								<TableCell>Ticket / Referencia</TableCell>
								<TableCell>Proveedor</TableCell>
								<TableCell>Pasajero</TableCell>
								<TableCell align="right">A pagar</TableCell>
								<TableCell align="right">Pagado</TableCell>
								<TableCell align="right">Pendiente</TableCell>
								<TableCell align="center">Estado</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{Object.entries(grouped).map(([serviceId, serviceLines]) =>
								serviceLines.map((line, idx) => {
									const remaining = line.payableAmount - line.totalPaid;

									return (
										<TableRow
											key={line.id}
											hover
											onClick={() => handleRowClick(line)}
											sx={{
												cursor: 'pointer',
												borderTop: idx === 0 && Object.keys(grouped).indexOf(serviceId) > 0 ? '2px solid' : undefined,
												borderTopColor: 'grey.200',
												'&:hover': { bgcolor: 'primary.50' },
											}}
										>
											<TableCell padding="checkbox">
												<Tooltip title={copiedLineId === line.id ? 'Copiado satisfactoriamente' : 'Copiar ID de línea'}>
													<IconButton size="small" onClick={(event) => handleCopyLineId(event, line.id)}>
														{copiedLineId === line.id ? (
															<CheckIcon fontSize="small" sx={{ color: 'green' }} />
														) : (
															<ContentCopyIcon fontSize="small" />
														)}
													</IconButton>
												</Tooltip>
											</TableCell>

											<TableCell>
												<Typography variant="caption" color="text.secondary">
													{PROVIDER_PAYABLE_LINE_TYPE_LABEL[line.payableType]}
												</Typography>
											</TableCell>

											<TableCell>
												{line.ticket ? (
													<Typography variant="body2" fontFamily="monospace">
														{line.ticket}
													</Typography>
												) : (
													<Typography variant="body2" color="text.secondary">
														{serviceId.substring(0, 8)}…
													</Typography>
												)}
											</TableCell>

											<TableCell>
												<Tooltip title={line.providerId}>
													<Typography variant="body2">{line.providerName ?? '-'}</Typography>
												</Tooltip>
											</TableCell>

											<TableCell>
												<Typography variant="body2">{line.passengerName ?? '-'}</Typography>
											</TableCell>

											<TableCell align="right">
												<Typography variant="body2" fontWeight={600}>
													USD{' '}
													{line.payableAmount.toLocaleString('es-UY', {
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													})}
												</Typography>
											</TableCell>

											<TableCell align="right">
												<Typography variant="body2">
													USD{' '}
													{line.totalPaid.toLocaleString('es-UY', {
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													})}
												</Typography>
											</TableCell>

											<TableCell align="right">
												<Typography
													variant="body2"
													fontWeight={600}
													color={remaining > 0 ? 'error.main' : 'success.main'}
												>
													USD{' '}
													{remaining.toLocaleString('es-UY', {
														minimumFractionDigits: 2,
														maximumFractionDigits: 2,
													})}
												</Typography>
											</TableCell>

											<TableCell align="center">
												<Chip
													label={PROVIDER_PAYABLE_LINE_STATUS_LABEL[line.paymentStatus]}
													color={PROVIDER_PAYABLE_LINE_STATUS_COLOR[line.paymentStatus]}
													size="small"
												/>
											</TableCell>
										</TableRow>
									);
								}),
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			<PaymentsModal
				key={selectedLine?.id ?? 'empty'}
				open={modalOpen}
				line={selectedLine}
				onClose={handleModalClose}
				onPaymentSaved={handlePaymentSaved}
			/>
		</Box>
	);
};
